import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, Search, Menu, X, Sparkles, User, LogOut, Package, Heart } from "lucide-react";
import { useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";
import { useWishlist } from "../lib/wishlist";
import api from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import BrandLogo from "./BrandLogo";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/find-my-fit", label: "Find My Fit" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const { count, setCartOpen } = useCart();
  const { user, logout, isStaff } = useAuth();
  const { count: wishCount } = useWishlist();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const immersive = pathname.startsWith("/find-my-fit") || pathname.startsWith("/symptom-checker");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (query.trim().length > 1) {
        try {
          const { data } = await api.post(`/ai/search-suggestions`, { query });
          setSuggestions(data.suggestions || []);
        } catch (_) {
          setSuggestions([]);
        }
      } else {
        setSuggestions([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  return (
    <>
      <header
  data-testid="main-navbar"
  className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
    scrolled
      ? "welljio-glass"
      : "bg-transparent"
  }`}
>
       <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo" aria-label="Welljio home" className="flex items-center group shrink-0">
            <BrandLogo className="h-10 sm:h-11 lg:h-12 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-12">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
  className={({ isActive }) =>
  `text-lg font-semibold tracking-[0.3px] transition-all duration-300 ${
    isActive
      ? "text-welljio-ink"
      : "text-slate-700 hover:text-welljio-ink"
  }`
}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              data-testid="nav-search-button"
              onClick={() => setSearchOpen(true)}
              className="w-9 h-9 rounded-full hover:bg-welljio-tint flex items-center justify-center transition-colors"
              aria-label="Search"
            >
              <Search className="w-5 h-5 text-welljio-ink" strokeWidth={1.8} />
            </button>
            <Link
              to="/wishlist"
              data-testid="nav-wishlist-button"
              className="relative w-9 h-9 rounded-full hover:bg-welljio-coralSoft flex items-center justify-center transition-colors"
              aria-label="Wishlist"
            >
              <Heart className={`w-5 h-5 ${wishCount > 0 ? "fill-welljio-coral text-welljio-coral" : "text-welljio-ink"}`} strokeWidth={1.8} />
              {wishCount > 0 && (
                <span
                  data-testid="wishlist-count-badge"
                  className="absolute -top-1 -right-1 bg-welljio-coral text-white text-[10px] font-semibold w-5 h-5 rounded-full flex items-center justify-center"
                >
                  {wishCount}
                </span>
              )}
            </Link>
            <button
              data-testid="nav-cart-button"
              onClick={() => setCartOpen(true)}
              className="relative w-9 h-9 rounded-full hover:bg-welljio-tint flex items-center justify-center transition-colors"
              aria-label="Cart"
            >
              <ShoppingBag className="w-5 h-5 text-welljio-ink" strokeWidth={1.8} />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-1 -right-1 bg-welljio-ink text-white text-[10px] font-semibold w-5 h-5 rounded-full flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </button>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-user-menu"
                    className="hidden sm:flex w-11 h-11 rounded-full bg-welljio-ink text-white items-center justify-center text-sm font-semibold hover:bg-slate-800"
                    aria-label="Account"
                  >
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 mt-2 rounded-2xl bg-white border-slate-100">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-welljio-ink line-clamp-1">{user.name}</p>
                    <p className="text-xs text-welljio-muted line-clamp-1">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="user-menu-account" onClick={() => navigate("/account")} className="rounded-xl cursor-pointer">
                    <User className="w-4 h-4 mr-2" strokeWidth={1.5}/> Account
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="user-menu-orders" onClick={() => navigate("/account")} className="rounded-xl cursor-pointer">
                    <Package className="w-4 h-4 mr-2" strokeWidth={1.5}/> Orders
                  </DropdownMenuItem>
                  {isStaff && (
                    <DropdownMenuItem data-testid="user-menu-admin" onClick={() => navigate("/admin")} className="rounded-xl cursor-pointer">
                      <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5}/> Admin Console
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="user-menu-logout" onClick={() => { logout(); navigate("/"); }} className="rounded-xl cursor-pointer text-welljio-muted">
                    <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5}/> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/login"
                data-testid="nav-signin-button"
                className="hidden sm:inline-flex items-center px-4 h-10 rounded-full bg-welljio-ink text-white text-sm font-medium hover:bg-slate-800"
              >
                Sign in
              </Link>
            )}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="mobile-menu-button"
                  className="lg:hidden w-11 h-11 rounded-full hover:bg-welljio-tint flex items-center justify-center"
                  aria-label="Menu"
                >
                  <Menu className="w-4 h-4 text-welljio-ink" strokeWidth={1.5} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-white w-[85vw] sm:w-[400px]" data-testid="mobile-menu-sheet">
                <SheetHeader>
                  <SheetTitle className="font-heading text-2xl text-welljio-ink">welljio</SheetTitle>
                </SheetHeader>
                <div className="mt-8 flex flex-col gap-2">
                  {NAV_LINKS.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="px-4 py-3 rounded-2xl hover:bg-welljio-tint text-welljio-ink font-medium"
                    >
                      {l.label}
                    </NavLink>
                  ))}
                  <div className="border-t border-slate-100 my-3" />
                  {user ? (
                    <>
                      <NavLink to="/account" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-account" className="px-4 py-3 rounded-2xl hover:bg-welljio-tint text-welljio-ink font-medium">Account & Orders</NavLink>
                      {isStaff && (
                       <NavLink to="/admin" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-admin" className="px-4 py-3 rounded-2xl hover:bg-welljio-tint text-welljio-ink font-medium">Admin Console</NavLink>
                      )}
                      <button onClick={() => { logout(); setMobileOpen(false); navigate("/"); }} data-testid="mobile-nav-logout" className="text-left px-4 py-3 rounded-2xl hover:bg-welljio-tint text-welljio-muted">Sign out</button>
                    </>
                  ) : (
                    <>
                      <NavLink to="/login" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-signin" className="px-4 py-3 rounded-2xl hover:bg-welljio-tint text-welljio-ink font-medium">Sign in</NavLink>
                      <NavLink to="/register" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-register" className="px-4 py-3 rounded-2xl hover:bg-welljio-tint text-welljio-ink font-medium">Create account</NavLink>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Search overlay */}
      {searchOpen && (
        <div
          data-testid="search-overlay"
          className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm animate-fade-in"
          onClick={() => setSearchOpen(false)}
        >
          <div
            className="absolute top-24 left-1/2 -translate-x-1/2 w-[92%] max-w-2xl bg-white rounded-3xl border border-slate-100 shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
              <Sparkles className="w-4 h-4 text-welljio-accent" strokeWidth={1.5} />
              <input
                data-testid="search-input"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Describe a symptom or product (e.g. neck pain)"
                className="flex-1 outline-none text-welljio-ink placeholder:text-welljio-subtle text-base"
              />
              <button
                data-testid="search-close-button"
                onClick={() => setSearchOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-welljio-tint flex items-center justify-center"
              >
                <X className="w-4 h-4 text-welljio-muted" strokeWidth={1.5} />
              </button>
            </div>
            <p className="text-xs text-welljio-subtle uppercase tracking-widest mt-4 mb-2">Smart Search</p>
            <div className="space-y-2 max-h-[55vh] overflow-y-auto">
              {suggestions.length === 0 && (
                <p className="text-sm text-welljio-muted py-6 text-center">
                  Try: <span className="text-welljio-ink">"neck pain"</span>,{" "}
                  <span className="text-welljio-ink">"tailbone"</span>, or{" "}
                  <span className="text-welljio-ink">"posture"</span>
                </p>
              )}
              {suggestions.map((s) => (
                <button
                  key={s.id}
                  data-testid={`search-result-${s.slug}`}
                  onClick={() => {
                    setSearchOpen(false);
                    setQuery("");
                    navigate(`/product/${s.slug}`);
                  }}
                  className="w-full flex items-center gap-4 p-3 rounded-2xl hover:bg-welljio-tint transition-colors text-left"
                >
                  <img src={s.images?.[0]} alt={s.name} className="w-14 h-14 rounded-xl object-cover bg-welljio-tint" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-welljio-ink truncate">{s.name}</p>
                    <p className="text-xs text-welljio-muted truncate">{s.category}</p>
                  </div>
                  <span className="text-sm text-welljio-muted">₹{s.price}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
